import { Box, type ContainerProps, Stack } from '@chakra-ui/react'
import { type ReactElement, useState } from 'react'

import Tabs from '@/components/Tabs/Tabs'

import Container from './Container'

const SECONDARY_COLUMN_WIDTH_PX = '300px'
const SPACING_PX = '24px'

type Tab = 'Primary' | 'Secondary'

export default function TwoColumnContainer({
  primaryColumn,
  secondaryColumn,
  secondaryColumnMobilePlacement = 'bottom',
  tabToLabel,
  reverseColumns = false,
  ...props
}: {
  primaryColumn: () => ReactElement
  secondaryColumn: () => ReactElement | null
  secondaryColumnMobilePlacement?: 'bottom' | 'top' | 'hide' | 'tab'
  tabToLabel?: Record<Tab, string>
  reverseColumns?: boolean
} & ContainerProps): JSX.Element {
  const [currentTab, setCurrentTab] = useState<Tab>('Primary')

  return (
    <Container {...props}>
      <Stack
        width="100%"
        direction={{
          base: secondaryColumnMobilePlacement === 'top' ? 'column-reverse' : 'column',
          md: reverseColumns ? 'row-reverse' : 'row',
        }}
        spacing={SPACING_PX}
      >
        <Box
          flex={1}
          maxWidth={{
            base: '100%',
            // We're explicit with the width here, so that children can properly set
            // maxWidth = 100%
            md: `calc(100% - ${SECONDARY_COLUMN_WIDTH_PX} - ${SPACING_PX})`,
          }}
        >
          {secondaryColumnMobilePlacement === 'tab' ? (
            <Box display={{ base: 'block', md: 'none' }}>
              <Tabs
                currentTab={currentTab}
                onTabChange={setCurrentTab}
                tabs={['Primary', 'Secondary']}
                tabToLabel={tabToLabel}
                mb="24px"
                hideBorder
              />
            </Box>
          ) : null}
          {currentTab === 'Primary' ? primaryColumn() : secondaryColumn()}
        </Box>
        <Box
          display={{
            base:
              secondaryColumnMobilePlacement === 'hide' || secondaryColumnMobilePlacement === 'tab'
                ? 'none'
                : 'flex',
            md: 'flex',
          }}
        >
          <Box width={{ base: '100%', md: SECONDARY_COLUMN_WIDTH_PX }}>{secondaryColumn()}</Box>
        </Box>
      </Stack>
    </Container>
  )
}
